import React, { useState, useEffect, useCallback } from "react";
import Image from "next/image";

const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:5000";
  return url.startsWith("/") ? `${base}${url}` : `${base}/${url}`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const Reporting = ({ campaignId }) => {
  const [updates, setUpdates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedImage, setExpandedImage] = useState(null);

  const fetchUpdates = useCallback(async () => {
    if (!campaignId) return;
    try {
      setIsLoading(true);
      setError(null);
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/campaign/${campaignId}/updates`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUpdates(data.updates || []);
    } catch (err) {
      console.error("Error fetching campaign updates:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  if (isLoading) {
    return (
      <div className="px-6 py-8 space-y-4">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="bg-white dark:bg-jacarta-700 rounded-2xl border border-jacarta-100 dark:border-jacarta-600 p-6 animate-pulse"
          >
            <div className="w-24 h-3 bg-jacarta-200 dark:bg-jacarta-600 rounded mb-4" />
            <div className="w-full h-4 bg-jacarta-200 dark:bg-jacarta-600 rounded mb-2" />
            <div className="w-3/4 h-4 bg-jacarta-200 dark:bg-jacarta-600 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="text-jacarta-500 dark:text-jacarta-400 mb-4">
          Failed to load updates.
        </p>
        <button
          onClick={fetchUpdates}
          className="px-5 py-2 bg-accent text-white rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (updates.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <svg
          className="mx-auto mb-4 h-14 w-14 text-jacarta-300 dark:text-jacarta-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <h3 className="text-lg font-semibold text-jacarta-700 dark:text-white mb-1">
          No updates yet
        </h3>
        <p className="text-sm text-jacarta-500 dark:text-jacarta-400">
          The campaign team hasn&apos;t posted any updates yet.
        </p>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 space-y-6">
      {updates.map((update) => (
        <div
          key={update._id}
          className="bg-white dark:bg-jacarta-700 rounded-2xl border border-jacarta-100 dark:border-jacarta-600 p-6 transition-shadow hover:shadow-lg"
        >
          <p className="text-xs text-jacarta-400 dark:text-jacarta-400 mb-3">
            {formatDate(update.createdAt)}
          </p>

          <p className="text-jacarta-700 dark:text-white whitespace-pre-line leading-relaxed">
            {update.body}
          </p>

          {update.attachments && update.attachments.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {update.attachments.map((url, i) => {
                const fullUrl = normalizeImageUrl(url);
                if (!fullUrl) return null;
                return (
                  <div
                    key={i}
                    className="relative aspect-square overflow-hidden rounded-xl cursor-pointer group border border-jacarta-100 dark:border-jacarta-600 hover:border-accent transition-colors"
                    onClick={() => setExpandedImage(fullUrl)}
                  >
                    <Image
                      src={fullUrl}
                      alt={`Update attachment ${i + 1}`}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => { e.currentTarget.src = "/images/placeholder.jpg"; }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}

      {/* Lightbox */}
      {expandedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative max-w-3xl w-full max-h-[80vh]">
            <Image
              src={expandedImage}
              alt="Expanded attachment"
              width={900}
              height={600}
              className="object-contain rounded-xl w-full h-full"
            />
            <button
              className="absolute top-3 right-3 w-8 h-8 bg-white/20 hover:bg-white/40 text-white rounded-full flex items-center justify-center transition-colors"
              onClick={() => setExpandedImage(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reporting;
